// The painter (docs/186): a fight board in one encounter set, as a flat picture of its ground and a list of
// everything that stands up off it. A pure function of the board, the set and the seed, so every client
// draws the same board (docs/15 §3.2). Nothing here reads a rule the sim doesn't hold: it only decides
// how the squares the forge made look.
import { boardSeed } from '../../tiling/hash';
import type { ArtLight, EncounterSet, PaintedBoard, Piece, Standing } from '../types';
import { Canvas, type RGBA } from './canvas';
import { Squares, type BoardIn } from './board';
import { C, Ctx, namedThicket } from './context';
import { groundOf, isLiquid, paintBanks, paintGrounds, type Ground } from './ground';
import { paintWalls } from './walls';
import { onTrail, trailSquares } from './trail';
import { paintChasms } from './chasm';
import { lookFloors, lookGround, paintRails, paintRunners, pillarFloor } from './look';
import type { Hooks } from './hooks';
import { hooks as country } from './country';
import { hooks as halls } from './halls';
import { hooks as places } from './places';

const FAMILIES: readonly Hooks[] = [country, halls, places];

export { Squares, type BoardIn } from './board';
export { Ctx, C } from './context';
export { Canvas } from './canvas';

/** The dark under a built kit's walls and over its rock (the tool's board background). */
const UNDER_WALLS: RGBA = [14, 13, 18, 255];
/** The lit colours (docs/132 OP17): kept bright at night, above the light map (ET9). */
export const LIT_COLOURS: readonly RGBA[] = [[0xff, 0xf0, 0xa8, 255], [0xff, 0xc9, 0x4a, 255], [0xf2, 0x92, 0x3a, 255]];

/** Decals that lie on water only (lily pads, lotus, candle boats, scum): docs/166 C2. */
const ON_WATER = /^decal_(lily|lotus|candleboat|scum|duckweed)/;
/** Decals laid by their own rules, never from the scatter. */
const OWN_RULE = /^decal_(cobwebs?|moss|heather|candles|oasis_reeds)$/;

export interface PaintOptions {
  /** The view seed; overrides `board.seed`. Without either, the board's tiles are hashed. */
  seed?: number;
  /** The set of the country the board's place stands in (`board.country`): its water, rough and thicket for a place that has none (EA14). */
  country?: EncounterSet;
}

export function paintBoard(board: BoardIn, set: EncounterSet, opts: PaintOptions = {}): PaintedBoard {
  const sq = new Squares(board);
  const seed = opts.seed ?? board.seed ?? boardSeed(board as Parameters<typeof boardSeed>[0]);
  const ctx = new Ctx(set, sq, seed, opts.country);
  const { w, h } = sq;
  const ground = new Canvas(w * C, h * C, ctx.built ? UNDER_WALLS : undefined);
  const standing: Standing[] = [];
  const lights: ArtLight[] = [];
  const liquids: Array<string | undefined> = new Array(w * h).fill(undefined);

  // 1. every square's ground (docs/164 "Ground"): the floor, seeded patches of `floor_patch`, rough, liquids, and
  // the rooms' own floors the forge names (EA13), over the kit's everyday floor but not a hook's special pieces
  const grounds: Array<Ground | undefined> = new Array(w * h).fill(undefined);
  const patchy = ctx.has('floor_patch');
  const floors = lookFloors(ctx);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.sq[y * w + x]!;
    if (s === 'void') continue;
    // a built kit's walls stand on nothing; but a lone wall is a pillar (EA6), and the floor runs on under it
    const pillar = s === 'wall' && ctx.has('stand') && !sq.isWall(x - 1, y) && !sq.isWall(x + 1, y) && !sq.isWall(x, y - 1) && !sq.isWall(x, y + 1);
    if (ctx.built && (s === 'wall' || s === 'rock') && !pillar) continue;
    let g: Ground | undefined;
    if (s === 'rough') g = groundOf(ctx, 'rough', 'rough', x, y);
    else if (s === 'water') g = groundOf(ctx, 'water', 'water', x, y);
    else if (s === 'deep') g = groundOf(ctx, 'deep', 'deep', x, y) ?? groundOf(ctx, 'water', 'water', x, y);
    // seeded patches with holes in them (`pine_board.py.txt`: a block of three in five, three squares in four)
    if (!g && patchy && s === 'floor' && !floors[y * w + x] && ctx.patch(x, y, 3, 0.3, 'floor_patch') && ctx.roll('patch hole', x, y) % 4) g = groundOf(ctx, 'floor_patch', 'floor_patch', x, y);
    g ??= groundOf(ctx, 'floor', 'floor', x, y);
    for (const f of FAMILIES) g = f.ground?.(ctx, x, y, s, g) ?? g;
    if (floors[y * w + x]) g = lookGround(ctx, floors, x, y, g);
    else if (pillar && (!g || g.name === 'floor')) { const n = pillarFloor(ctx, floors, x, y); if (n) g = groundOf(ctx, n, n, x, y) ?? g; }
    grounds[y * w + x] = g;
    if (g && isLiquid(g.name)) liquids[y * w + x] = g.name;
  }
  paintGrounds(ctx, ground, grounds);
  // the trail between the ways in and out, worn into plain floor only (docs/166 C2, E4)
  if (ctx.has('floor_path')) {
    const trail = trailSquares(ctx);
    const path = ctx.all('floor_path');
    const near = new Set<number>();
    for (const i of trail) {
      const x0 = i % w, y0 = Math.floor(i / w);
      for (let y = Math.max(0, y0 - 1); y <= Math.min(h - 1, y0 + 1); y++) for (let x = Math.max(0, x0 - 1); x <= Math.min(w - 1, x0 + 1); x++) near.add(y * w + x);
    }
    for (const i of near) {
      const x = i % w, y = Math.floor(i / w);
      if (grounds[i]?.name !== 'floor' && grounds[i]?.name !== 'floor_patch') continue;
      const p = path[ctx.roll('pt', x, y) % path.length]!;
      for (let v = 0; v < C; v++) for (let u = 0; u < C; u++) if (onTrail(ctx, trail, x * C + u, y * C + v)) ground.copy(x * C + u, y * C + v, p, u, v);
    }
  }
  // the forge's runners and rails, over the floor (EA13)
  paintRunners(ctx, ground, floors);
  paintRails(ctx, ground);
  if (!FAMILIES.some((f) => f.banks?.(ctx, ground, grounds))) paintBanks(ctx, ground, grounds, (x, y) => ctx.built && sq.isWall(x, y));
  paintChasms(ctx, ground, grounds);
  for (const f of FAMILIES) f.afterGround?.(ctx, ground, grounds);

  // 2. what lies flat on a square: exits, hazards' ground, ice, tunnel mouths, mist, the clump under a thicket
  const lay = (piece: string, x: number, y: number, purpose = piece): Piece | undefined => {
    const p = ctx.pick(piece, purpose, x, y);
    if (p) ground.draw(p, x * C + C / 2 - p.w / 2, y * C + C - p.h);
    return p;
  };
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.sq[y * w + x]!, t = sq.tile(x, y);
    if (!t || s === 'void') continue;
    if (s === 'tunnel') lay('tunnel', x, y);
    if (t.hazard) {
      const fire = t.hazard === 'Fire' || t.hazard === 'Burning';
      // docs/166 C2 (the bricks): `hazard_deep` is drawn on `deep` and goes in deep squares only
      if (fire) { lay('hazard_fire', x, y); lights.push({ x: x * C + C / 2, y: y * C + C / 2, kind: 'fire' }); }
      else if (t.hazard === 'Steam') lay('hazard_steam', x, y);
      else if (t.hazard === 'DeepWater' && s === 'deep') lay('hazard_deep', x, y);
    }
    // (a family that made the square's ground its ice, frayed and lined, has laid it already)
    if (t.ice && grounds[y * w + x]?.piece.piece !== 'ice') lay('ice', x, y);
    if (s === 'thicket') lay('thicket_under', x, y);
    if (t.veil) lay('veil', x, y);
    if (t.exit && s !== 'wall' && s !== 'rock') lay('exit', x, y);
    // a door in a wall that runs up the board, or any door of a kit with no walls, lies flat (docs/164 "Ways through")
    if ((s === 'door' || s === 'way') && (!ctx.built || (sq.solid(x, y - 1) && sq.solid(x, y + 1)))) {
      const side = sq.solid(x, y - 1) && sq.solid(x, y + 1);
      const name = s === 'way' ? `hidden_way_${side ? 'side' : 'front'}` : `door_${t.doorOpen && ctx.has(`door_open_${side ? 'side' : 'front'}`) ? 'open' : 'closed'}_${side ? 'side' : 'front'}`;
      lay(name, x, y, 'door');
    }
  }

  // 3. decals: small, low and seeded; each kind on its own ground (docs/164 ET2 "decoration stays off the squares' business")
  const decals = ctx.named('decal_').filter((d) => !OWN_RULE.test(d) && !FAMILIES.some((f) => f.ownsDecal?.(ctx, d)));
  const landDecals = decals.filter((d) => !ON_WATER.test(d)), waterDecals = decals.filter((d) => ON_WATER.test(d));
  const rate = DECAL_RATE[set.kit] ?? 6;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.sq[y * w + x]!, t = sq.tile(x, y);
    if (!t || t.exit || t.hazard || t.objective || t.ice) continue;
    const pool = s === 'floor' ? landDecals : s === 'water' ? waterDecals : [];
    if (!pool.length || ctx.roll('decal', x, y) % 100 >= rate) continue;
    lay(pool[ctx.roll('decal kind', x, y) % pool.length]!, x, y, 'decal pick');
  }
  // stray moss beside a patch (docs/164 "The pinewood's look"): the patch thins out into the floor
  if (ctx.has('decal_moss') && patchy) {
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (sq.sq[y * w + x] !== 'floor' || grounds[y * w + x]?.name !== 'floor') continue;
      const near = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => grounds[(y + dy!) * w + x + dx!]?.name === 'floor_patch' && sq.on(x + dx!, y + dy!));
      if (near && ctx.roll('moss', x, y) % 100 < 75) lay('decal_moss', x, y);
    }
  }
  // cobwebs in the corners of a room (one per corner): mirrored for the north-east
  if (ctx.has('decal_cobwebs')) {
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (sq.sq[y * w + x] !== 'floor' || !sq.isWall(x, y - 1) || ctx.roll('web', x, y) % 100 >= 35) continue;
      const nw = sq.isWall(x - 1, y), ne = sq.isWall(x + 1, y);
      if (!nw && !ne) continue;
      const p = ctx.pick('decal_cobwebs', 'web pick', x, y)!;
      ground.draw(p, x * C, y * C, ne && !nw ? { flipX: true } : undefined);
    }
  }

  for (const f of FAMILIES) f.afterFlat?.(ctx, ground, grounds, lights);

  // 4. walls: a built kit's raised walls; a country's trees; pillars, thickets and cover (docs/164 ET19)
  let headroom = 0;
  if (ctx.built) {
    let walls = undefined as ReturnType<typeof paintWalls> | undefined;
    for (const f of FAMILIES) walls ??= f.walls?.(ctx, ground);
    walls ??= paintWalls(ctx, ground);
    standing.push(...walls.standing);
    lights.push(...walls.lights);
    headroom = walls.headroom;
  }
  const stand = (piece: Piece | undefined, x: number, y: number, kind: Standing['kind'], flipX = false): void => {
    if (!piece) return;
    const px = x * C + C / 2 - piece.w / 2, py = (y + 1) * C - piece.h;
    // its contact shadow lies on the ground under it (the tool's 18×6 ellipse at the foot)
    const cx = px + piece.w / 2, cy = py + piece.h - 2;
    ground.ellipse(cx - 9, cy - 3, cx + 9, cy + 3, [10, 20, 10, 80]);
    // a piece borrowed from the country (EA14) is not in the set's texture: it stands as its own pixels
    const art = ctx.own(piece) ? { frame: piece.frame, ...(flipX ? { flipX } : {}) } : { px: flipX ? mirrored(piece) : piece.px };
    standing.push({ kind, at: { x, y }, x: px, y: py, w: piece.w, h: piece.h, foot: y * C + C / 2, ...art });
    headroom = Math.max(headroom, -py);
  };
  const groves = ctx.named('grove_');
  const thicket = namedThicket(set) && !ctx.has('thicket') ? namedThicket(set)! : 'thicket';
  const claimed = new Set<number>();
  for (const f of FAMILIES) for (const c of f.claim?.(ctx) ?? []) claimed.add(c);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.sq[y * w + x]!, t = sq.tile(x, y);
    if (!t || claimed.has(y * w + x)) continue;
    if ((s === 'wall' || s === 'rock') && !ctx.built) {
      // trees on every wall square; inside a seeded patch a grove's own trees (fewer than stands, docs/164 notes)
      let piece = 'stand';
      if (groves.length && ctx.patch(x, y, 4, 0.35, 'grove')) {
        const g = groves[ctx.roll('grove kind', Math.floor(x / 4), Math.floor(y / 4)) % groves.length]!;
        // ricks and crags go singly among trees, never as a whole patch (docs/166 C2, the wetland Snowfield)
        if (!/crags|boulder|outcrop/.test(g) || ctx.roll('single', x, y) % 4 === 0) piece = g;
      }
      stand(ctx.pick(piece, 's', x, y) ?? ctx.pick('stand', 's', x, y), x, y, piece === 'stand' ? 'stand' : 'grove', ctx.roll('flip', x, y) % 2 === 1);
    } else if (s === 'wall' && ctx.built && ctx.has('stand') && !sq.isWall(x - 1, y) && !sq.isWall(x + 1, y) && !sq.isWall(x, y - 1) && !sq.isWall(x, y + 1)) {
      stand(ctx.pick('stand', 'p', x, y), x, y, 'stand');
    } else if (s === 'thicket') {
      stand(ctx.pick(thicket, 't', x, y), x, y, 'thicket', ctx.roll('flip', x, y) % 2 === 1);
    } else if ((s === 'floor' || s === 'rough') && t.cover > 0 && ctx.has('cover')) {
      stand(ctx.pick('cover', 'c', x, y), x, y, 'cover');
    }
  }

  // the board ending inside its country: a stand of the country's trees along the void beside it (docs/164 "Cover, edge")
  if (!ctx.built && ctx.has('edge_stand')) {
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (sq.sq[y * w + x] !== 'void') continue;
      const by = [[0, 1], [1, 0], [-1, 0], [0, -1]].map(([dx, dy]) => [x + dx!, y + dy!] as const).find(([a, b]) => sq.on(a, b) && sq.sq[b * w + a] !== 'void');
      if (!by || ctx.roll('edge', x, y) % 3) continue;
      const p = ctx.pick('edge_stand', 'e', x, y)!;
      const px = x * C + C / 2 - p.w / 2, py = (y + 1) * C - p.h;
      // it takes its light and fog from the board square beside it
      standing.push({ kind: 'edge', at: { x: by[0], y: by[1] }, x: px, y: py, w: p.w, h: p.h, foot: y * C + C / 2, frame: p.frame, ...(ctx.roll('flip', x, y) % 2 ? { flipX: true } : {}) });
      headroom = Math.max(headroom, -py);
    }
  }

  for (const f of FAMILIES) f.afterStanding?.(ctx, ground, grounds, standing, lights);
  headroom = Math.max(headroom, ...standing.map((st) => -st.y));

  // 5. night: the lit colours of the ground, kept bright over the light map (ET9)
  let lit: Uint8ClampedArray | undefined;
  if (set.time === 'night' || set.indoor) {
    const px = ground.px;
    for (let i = 0; i < px.length; i += 4) {
      // every lit colour is bright red (≥ 0xf2): most pixels are out at the first test
      if (px[i]! < 0xf2 || !px[i + 3]) continue;
      const g = px[i + 1]!, b = px[i + 2]!;
      if (!LIT_COLOURS.some((c) => c[0] === px[i] && c[1] === g && c[2] === b)) continue;
      lit ??= new Uint8ClampedArray(px.length);
      lit[i] = px[i]!; lit[i + 1] = px[i + 1]!; lit[i + 2] = px[i + 2]!; lit[i + 3] = 255;
    }
  }

  standing.sort((a, b) => a.foot - b.foot || a.y - b.y || a.x - b.x);
  return { w, h, cell: C, ground: ground.px, ...(lit ? { lit } : {}), standing, lights, liquids, headroom, ...(ctx.ledges ? { ledges: true } : {}) };
}

/** A piece's pixels mirrored left to right. */
function mirrored(p: Piece): Uint8ClampedArray {
  const out = new Uint8ClampedArray(p.px.length);
  for (let v = 0; v < p.h; v++) for (let u = 0; u < p.w; u++) out.set(p.px.subarray((v * p.w + u) * 4, (v * p.w + u) * 4 + 4), (v * p.w + p.w - 1 - u) * 4);
  return out;
}

/** Per cent of open squares with a decal; quieter where a decal reads as an item (docs/164 notes: the Underdark's caps, the Ashlands' vents, the jungle's fruit). */
const DECAL_RATE: Record<string, number> = { underdark: 3, ashlands: 3, jungle: 4 };
